import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  MapPin, Phone, Star, Camera, CheckCircle, Clock, Award,
  MessageCircle, ChevronRight, Shield, ArrowRight, X, Calendar,
  Search, Eye, Users, TrendingUp, Store, Building2, Utensils,
  Factory, ShoppingCart, Wine, Briefcase, Target
} from "lucide-react";
import { ContactSection, SectionDivider } from "@/Layout";
import { WA_LINK, CALENDLY } from "@/Layout";
import { WhatsAppBookingForm } from "@/components/WhatsAppBookingForm";
import { CrossSellingGift, CrossSellingProducts } from "@/components/CrossSelling";
import { TShirtBundle } from "@/components/TShirtBundle";

const StarRating = ({ rating, count }) => (
  <div className="flex items-center gap-1">
    {[1,2,3,4,5].map(s => (
      <Star key={s} className={`w-4 h-4 ${s <= Math.floor(rating) ? 'text-[#FFA41C] fill-[#FFA41C]' : s <= rating ? 'text-[#FFA41C] fill-[#FFA41C] opacity-50' : 'text-gray-300'}`} />
    ))}
    <span className="text-sm text-blue-600 ml-1">({count})</span>
  </div>
);

// Luxury Pricing Card
const LuxuryPricingCard = () => (
  <section className="py-16 bg-[#0c1220]">
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
      <Link to="/" className="inline-flex items-center gap-2 text-gray-500 hover:text-[#ff6b35] mb-8 transition-colors" data-testid="back-home">
        <ChevronRight className="w-4 h-4 rotate-180" /> Torna alle 4 porte
      </Link>
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 mb-4">
          <span className="w-4 h-4 rounded-full bg-[#34A853]"></span>
          <Badge className="bg-green-100 text-[#34A853] border-green-300">TUTTO INCLUSO + ASSISTENZA PREMIUM</Badge>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
          Rooftop — <span className="text-amber-600">TUTTO Incluso + Assistenza Premium</span>
        </h1>
        <p className="text-gray-500">La soluzione completa per dominare la ricerca locale</p>
      </div>

      <div className="bg-gradient-to-b from-amber-50 to-white rounded-2xl border-2 border-amber-400 shadow-xl relative overflow-hidden">
        <div className="bg-gradient-to-r from-[#34A853] to-[#2d9649] text-white text-center py-2 text-sm font-black tracking-wide">TUTTO INCLUSO + ASSISTENZA PREMIUM</div>
        <div className="p-6 md:p-8">
          <StarRating rating={5} count={23} />
          <div className="mt-3 mb-2">
            <div className="flex items-baseline">
              <span className="text-sm text-gray-400 mr-1">€</span>
              <span className="text-5xl font-bold text-[#34A853] tracking-tight">599</span>
            </div>
            <p className="text-xs text-gray-500">una tantum · oppure €49,92/mese x12</p>
          </div>
          <div className="bg-amber-50 border border-amber-500/20 rounded-lg px-3 py-2 mb-4">
            <p className="text-xs text-[#34A853] font-bold"><Clock className="w-3 h-3 inline mr-1" />Per chi vuole <strong>farsi trovare</strong> a livello locale</p>
          </div>

          {/* What's included */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-4 mb-4">
            <p className="text-green-800 text-xs font-bold mb-2"><Award className="w-4 h-4 inline mr-1 text-green-600" />Il Rooftop include TUTTO:</p>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-gray-400">Start (verifica, claim, correzione)</span><span className="text-gray-200">€99</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Plus (sospensione, recensioni, consulenza)</span><span className="text-gray-200">€299</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Virtual Tour 360</span><span className="text-gray-200">€499</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Assistenza premium 12 mesi</span><span className="text-gray-200">valore inestimabile</span></div>
              <div className="flex justify-between bg-green-100 -mx-1 px-1 py-1.5 rounded-lg mt-1"><span className="text-[#34A853] font-bold">TU paghi tutto questo a</span><span className="text-[#34A853] font-black text-base">€599</span></div>
            </div>
          </div>

          <ul className="space-y-2 mb-6">
            {["Tutto di Start + Plus incluso", "Virtual Tour 360 incluso", "Almeno 15 foto a 360 in REGALO", "3 foto sferiche/immersive in REGALO", "Assistenza premium dedicata", "Supporto prioritario 12 mesi", "Risposta recensioni illimitata", "Citazioni NAP 20+ directory", "Schema markup LocalBusiness", "Monitoraggio competitor", "Report settimanale", "Supporto prioritario WhatsApp"].map((f, i) => (
              <li key={i} className="flex items-start gap-2 border border-white/20 rounded-lg px-3 py-2 bg-white/[0.03]"><CheckCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${f.includes('REGALO') ? 'text-[#ff6b35]' : 'text-[#34A853]'}`} /><span className={f.includes('REGALO') ? 'text-[#ff6b35] font-bold' : 'text-gray-300'}>{f}</span></li>
            ))}
          </ul>

          <button className="w-full bg-[#FFD814] hover:bg-[#F7CA00] text-gray-900 font-bold py-4 rounded-full border border-[#FCD200] shadow-md transition-all active:scale-95 text-lg relative overflow-hidden group"
            onClick={() => window.open('https://www.paypal.me/Riccarossi/599', '_blank')} data-testid="luxury-pkg-btn">
            <span className="relative z-10 inline-flex items-center justify-center gap-2">
              <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.771.771 0 0 1 .761-.652h6.199c2.063 0 3.543.585 4.268 1.694.335.516.538 1.087.617 1.742.083.677.032 1.478-.157 2.398l-.013.063v.564l.44.249c.372.21.67.455.893.735.37.464.607 1.042.7 1.715.096.693.037 1.498-.173 2.393-.241 1.028-.635 1.922-1.171 2.655a5.292 5.292 0 0 1-1.539 1.546c-.593.39-1.298.672-2.097.84-.769.161-1.64.245-2.582.245h-.614a1.793 1.793 0 0 0-1.77 1.513l-.046.244-.77 4.88-.036.174a.164.164 0 0 1-.047.1.13.13 0 0 1-.078.028z" fill="#253B80"/><path d="M18.36 7.667a7.37 7.37 0 0 0-.078.449c-1 5.155-4.426 6.933-8.801 6.933h-2.228a1.08 1.08 0 0 0-1.07.918L5.09 22.83l-.323 2.054a.57.57 0 0 0 .563.66h3.952a.95.95 0 0 0 .94-.803l.038-.201.744-4.728.048-.26a.951.951 0 0 1 .94-.804h.591c3.83 0 6.828-1.556 7.704-6.063.366-1.882.176-3.453-.793-4.558a3.793 3.793 0 0 0-1.082-.835z" fill="#179BD7"/></svg>
              <span className="cta-wave-text">Paga con PayPal — €599</span>
            </span>
            <span className="cta-talpa-shine"></span>
          </button>
          <div className="flex items-center justify-center gap-2 mt-2">
            <svg className="h-4 w-auto" viewBox="0 0 101 32" fill="#003087"><path d="M12.2 4.6h-7c-.5 0-.9.3-1 .8L1.5 24c-.1.4.2.7.6.7h3.4c.5 0 .9-.3 1-.8l.8-5.1c.1-.5.5-.8 1-.8h2.3c4.7 0 7.5-2.3 8.2-6.8.3-2 0-3.5-.9-4.6C16.8 5.3 14.8 4.6 12.2 4.6z"/><path d="M39.6 4.6h-7c-.5 0-.9.3-1 .8l-2.7 18.6c-.1.4.2.7.6.7h3.6c.3 0 .6-.2.7-.6l.8-5.3c.1-.5.5-.8 1-.8h2.3c4.7 0 7.5-2.3 8.2-6.8.3-2 0-3.5-.9-4.6C44.2 5.3 42.2 4.6 39.6 4.6z"/></svg>
            <span className="text-xs text-gray-500">oppure <strong className="text-[#003087]">€199,67 x 3 rate</strong> · TAEG 0%</span>
          </div>
        </div>
      </div>

      {/* Comparison table */}
      <div className="mt-10">
        <h4 className="font-bold text-white mb-4 flex items-center gap-2"><Search className="w-5 h-5 text-gray-500" />Confronta i pacchetti</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="compare-table">
            <thead>
              <tr className="border-b-2 border-white/10">
                <th className="text-left py-3 px-3 text-gray-500 font-medium">Funzionalità</th>
                <th className="text-center py-3 px-3 text-blue-600 font-bold">Start<br/><span className="text-xs font-normal text-gray-400">€99</span></th>
                <th className="text-center py-3 px-3 text-red-600 font-bold">Plus<br/><span className="text-xs font-normal">€299</span></th>
                <th className="text-center py-3 px-3 text-teal-600 font-bold">Virtual Tour<br/><span className="text-xs font-normal text-gray-400">€499</span></th>
                <th className="text-center py-3 px-3 text-amber-700 font-bold bg-amber-50/50 rounded-t-lg">Rooftop<br/><span className="text-xs font-normal text-amber-500">€599</span></th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: "Verifica e claim scheda", start: true, plus: true, vt360: true, luxury: true },
                { feature: "Correzione info (NAP)", start: true, plus: true, vt360: true, luxury: true },
                { feature: "Foto profilo", start: "1", plus: "5", vt360: "1", luxury: "Illimitate" },
                { feature: "Recupero sospensione", start: false, plus: true, vt360: false, luxury: true },
                { feature: "Risposta recensioni", start: false, plus: true, vt360: false, luxury: "Illimitata" },
                { feature: "Consulenza strategica", start: false, plus: "1h", vt360: false, luxury: "Prioritaria" },
                { feature: "Virtual Tour 360", start: false, plus: false, vt360: true, luxury: true },
                { feature: "Assistenza premium dedicata", start: false, plus: false, vt360: false, luxury: true },
                { feature: "Gestione 12 mesi", start: false, plus: false, vt360: false, luxury: true },
                { feature: "Schema markup", start: false, plus: false, vt360: false, luxury: true },
                { feature: "Monitoraggio competitor", start: false, plus: false, vt360: false, luxury: true },
                { feature: "Report", start: "Iniziale", plus: "Mensile", vt360: "Iniziale", luxury: "Settimanale" },
                { feature: "Supporto WhatsApp", start: false, plus: "Standard", vt360: false, luxury: "Prioritario" },
                { feature: "Rata mensile", start: "€8,25", plus: "€33,25", vt360: "€41,58", luxury: "€49,92" },
              ].map((row, i) => (
                <tr key={i} className={`border-b border-white/10 ${i % 2 === 0 ? 'bg-white/5' : ''}`}>
                  <td className="py-2.5 px-3 text-gray-300">{row.feature}</td>
                  <td className="py-2.5 px-3 text-center">{row.start === true ? <CheckCircle className="w-4 h-4 text-blue-400 mx-auto" /> : row.start === false ? <X className="w-4 h-4 text-gray-300 mx-auto" /> : <span className="text-xs text-gray-400">{row.start}</span>}</td>
                  <td className="py-2.5 px-3 text-center">{row.plus === true ? <CheckCircle className="w-4 h-4 text-red-500 mx-auto" /> : row.plus === false ? <X className="w-4 h-4 text-gray-300 mx-auto" /> : <span className="text-xs font-bold text-red-500">{row.plus}</span>}</td>
                  <td className="py-2.5 px-3 text-center">{row.vt360 === true ? <CheckCircle className="w-4 h-4 text-teal-500 mx-auto" /> : row.vt360 === false ? <X className="w-4 h-4 text-gray-300 mx-auto" /> : <span className="text-xs font-bold text-teal-600">{row.vt360}</span>}</td>
                  <td className="py-2.5 px-3 text-center bg-amber-50/30">{row.luxury === true ? <CheckCircle className="w-4 h-4 text-amber-500 mx-auto" /> : row.luxury === false ? <X className="w-4 h-4 text-gray-300 mx-auto" /> : <span className="text-xs font-bold text-amber-600">{row.luxury}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 flex justify-center gap-4">
        <Link to="/start" className="text-sm text-gray-500 hover:text-blue-600 transition-colors">← Start €99</Link>
        <Link to="/plus" className="text-sm text-gray-500 hover:text-red-600 transition-colors">← Plus €299</Link>
      </div>
    </div>
  </section>
);

// Local SEO Stats
const LocalSeoStatsSection = () => (
  <section className="py-16 bg-gray-900 text-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <Badge className="bg-[#ff6b35]/20 text-[#ff6b35] border-[#ff6b35]/30 mb-4"><TrendingUp className="w-4 h-4 mr-2" />Ricerca di Mercato</Badge>
        <h2 className="text-3xl md:text-4xl font-bold mb-4">Perché la Tua Scheda Google è Fondamentale</h2>
        <p className="text-gray-400 max-w-3xl mx-auto">Un'azienda indipendente di ricerche di mercato ha studiato quanto i tour virtuali contribuiscono ad accrescere l'interesse dei consumatori per le attività commerciali locali.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
        {[
          { value: "44%", text: "Quando cercano un'attività commerciale, i consumatori si affidano agli strumenti di mappatura.", color: "text-red-500", hover: "hover:border-red-500/50" },
          { value: "41%", text: "In media, il 41% di queste ricerche porta a una visita in loco.", color: "text-yellow-500", hover: "hover:border-yellow-500/50" },
          { value: "76%", text: "Il 76% delle persone che fa una ricerca \"vicino a me\" visita un'attività nelle 24 ore.", color: "text-green-500", hover: "hover:border-green-500/50" },
          { value: "28%", text: "Il 28% di chi fa una ricerca \"vicino a me\" compra.", color: "text-blue-400", hover: "hover:border-blue-500/50" },
        ].map((stat, i) => (
          <Card key={i} className={`bg-gray-800/50 border-gray-700 ${stat.hover} transition-all`}>
            <CardContent className="p-6 text-center">
              <div className={`text-5xl font-bold ${stat.color} mb-4`}>{stat.value}</div>
              <p className="text-gray-300 text-sm">{stat.text}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="text-center mt-12">
        <p className="text-[#ff6b35] text-lg font-semibold">La <strong>TUA</strong> scheda Google ottimizzata = <strong>più</strong> clienti per <strong>TE</strong>!</p>
      </div>
    </div>
  </section>
);

// LOCAL SEO PRESENTATION - "Hai Fatto un'Ottima Scelta"
const LocalSeoPresentationSection = () => {
  const stats = [
    { label: "Ricerche locali su Google", value: 46, suffix: "%", color: "#F59E0B", desc: "Quasi la meta' delle ricerche Google ha intento locale" },
    { label: "In piu' richieste indicazioni", value: 70, suffix: "%", color: "#34A853", desc: "Con un profilo Google ottimizzato" },
    { label: "Si fida delle recensioni online", value: 84, suffix: "%", color: "#4285F4", desc: "Tanto quanto delle raccomandazioni personali" },
    { label: "Vendite in piu' con SEO locale", value: 29, suffix: "%", color: "#EA4335", desc: "Aumento medio per chi investe nella Local SEO" },
  ];

  return (
    <section className="py-16 bg-[#0c1220]" data-testid="local-seo-presentation">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="bg-amber-100 text-amber-700 border-amber-300 mb-4 rounded-full px-4 py-1.5">
            <Shield className="w-4 h-4 mr-2" />
            Per chi ha scelto Rooftop
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-white font-google mb-3">
            Hai fatto un'<span className="text-amber-600">ottima</span> scelta
          </h2>
          <p className="text-gray-500 text-sm md:text-base max-w-2xl mx-auto">
            Non hai comprato un pacchetto. Hai investito nel dispositivo piu' potente per la visibilita' locale: la <strong>Local SEO</strong>.
          </p>
        </div>

        {/* Visual: Search to Visit Funnel */}
        <div className="grid md:grid-cols-2 gap-8 mb-14 items-center">
          <div>
            <h3 className="text-xl font-bold text-white mb-4 font-google">Da ricerca a cliente in 3 passi</h3>
            <div className="space-y-4">
              {[
                { step: "1", title: "Il cliente cerca", desc: "\"ristorante vicino a me\", \"idraulico Siena\", \"hotel Colle Val d'Elsa\"", color: "bg-blue-500" },
                { step: "2", title: "Google mostra la TUA scheda", desc: "Con Rooftop, la TUA scheda appare in cima con foto, stelle e indicazioni", color: "bg-amber-500" },
                { step: "3", title: "Il cliente TI contatta", desc: "Chiama, chiede indicazioni o viene direttamente da TE", color: "bg-green-500" },
              ].map((s) => (
                <div key={s.step} className="flex gap-3 items-start">
                  <div className={`w-8 h-8 ${s.color} rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>{s.step}</div>
                  <div>
                    <p className="font-bold text-white text-sm">{s.title}</p>
                    <p className="text-xs text-gray-500">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-center">
            <img src="https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/0253dd4974b99c16c7685534d2d334ce5992800848dc488074c8dcc1e132e0d9.png"
              alt="Da ricerca locale a visita" className="w-64 md:w-80 rounded-2xl shadow-lg" />
          </div>
        </div>

        {/* Stats with SVG Ring Charts */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((s, i) => (
            <div key={i} className="bg-white/5 rounded-2xl p-4 text-center border border-white/10">
              <div className="relative w-24 h-24 mx-auto mb-3">
                <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="#e5e7eb" strokeWidth="8" />
                  <circle cx="50" cy="50" r="42" fill="none" stroke={s.color} strokeWidth="8"
                    strokeDasharray={`${s.value * 2.64} ${264 - s.value * 2.64}`}
                    strokeLinecap="round" className="transition-all duration-1000" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xl font-black text-white">{s.value}{s.suffix}</span>
                </div>
              </div>
              <p className="text-xs font-bold text-gray-300 mb-0.5">{s.label}</p>
              <p className="text-[10px] text-gray-400">{s.desc}</p>
            </div>
          ))}
        </div>

        {/* Before / After */}
        <div className="mb-14">
          <h3 className="text-xl font-bold text-white text-center mb-6 font-google">Prima e dopo <span className="text-amber-600">Rooftop</span></h3>
          <div className="rounded-2xl overflow-hidden border border-white/10">
            <img src="https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/ad457cf266977af4d7db3c7b819f2bbdb77f829dc95a3796302ec2558b4f38de.png"
              alt="Prima e Dopo Rooftop SEO" className="w-full" />
          </div>
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div className="bg-red-900/20 border border-red-500/20 rounded-xl p-4">
              <h4 className="font-bold text-red-700 text-sm mb-2">PRIMA — Senza SEO Locale</h4>
              <ul className="text-xs text-red-600 space-y-1">
                {["Info incomplete, orari mancanti", "Zero foto professionali", "Nessuna risposta alle recensioni", "Non appari nelle ricerche 'vicino a me'", "I clienti vanno dalla concorrenza"].map((t, i) => (
                  <li key={i} className="flex items-center gap-1.5"><X className="w-3 h-3 flex-shrink-0" />{t}</li>
                ))}
              </ul>
            </div>
            <div className="bg-green-900/20 border border-green-500/20 rounded-xl p-4">
              <h4 className="font-bold text-green-700 text-sm mb-2">DOPO ROOFTOP — Con SEO Locale</h4>
              <ul className="text-xs text-green-600 space-y-1">
                {["Scheda completa e ottimizzata", "Foto professionali + Virtual Tour 360°", "Recensioni gestite e monitorate", "In cima alle ricerche locali", "I clienti trovano TE per primo"].map((t, i) => (
                  <li key={i} className="flex items-center gap-1.5"><CheckCircle className="w-3 h-3 flex-shrink-0" />{t}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* ROI Simple */}
        <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 border-2 border-amber-300 rounded-2xl p-6 md:p-8 mb-14">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div>
              <h3 className="text-lg font-bold text-white mb-3 font-google">Il conto e' semplice</h3>
              <div className="space-y-3">
                <div className="bg-white/5 rounded-xl p-3 border border-amber-500/20">
                  <p className="text-xs text-gray-500">Il TUO investimento Rooftop</p>
                  <p className="text-2xl font-black text-amber-700">€599</p>
                  <p className="text-[10px] text-gray-400">una tantum · oppure €49,92/mese</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3 border border-amber-500/20">
                  <p className="text-xs text-gray-500">Se grazie alla SEO locale acquisti anche solo</p>
                  <p className="text-2xl font-black text-green-600">1 cliente in piu' al mese</p>
                  <p className="text-[10px] text-gray-400">quanto vale un singolo cliente per la TUA attivita'?</p>
                </div>
              </div>
            </div>
            <div className="text-center">
              <img src="https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/4bb2da2aaa1f6976c948b41f2bc59fd7c8466b9f2ced244742a088bd8941ae33.png"
                alt="Essere trovati per primi su Google" className="w-48 md:w-56 mx-auto rounded-2xl shadow-lg" />
              <p className="text-xs text-amber-700 font-bold mt-3">Essere trovati PER PRIMI su Google</p>
            </div>
          </div>
        </div>

        {/* Cosa include il Rooftop - Quick recap */}
        <div className="text-center mb-8">
          <h3 className="text-xl font-bold text-white mb-4 font-google">Tutto questo e' <span className="text-amber-600">gia' incluso</span> nel TUO Rooftop</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { icon: CheckCircle, text: "Verifica e claim scheda", sub: "Start incluso" },
              { icon: Shield, text: "Recupero sospensione", sub: "Plus incluso" },
              { icon: Camera, text: "Virtual Tour 360", sub: "Virtual Tour incluso" },
              { icon: TrendingUp, text: "Assistenza premium 12 mesi", sub: "Solo Rooftop" },
              { icon: Eye, text: "Monitoraggio competitor", sub: "Solo Rooftop" },
              { icon: Target, text: "Report settimanale", sub: "Solo Rooftop" },
            ].map((f, i) => (
              <div key={i} className="bg-white/5 rounded-xl p-3 border border-white/10 text-left">
                <div className="flex items-center gap-2 mb-1">
                  <f.icon className="w-4 h-4 text-amber-500" />
                  <span className="text-xs font-bold text-gray-200">{f.text}</span>
                </div>
                <span className="text-[10px] text-gray-400">{f.sub}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Final Reassurance */}
        <div className="bg-gray-900 rounded-2xl p-6 text-center">
          <p className="text-amber-400 font-bold text-sm mb-1 font-google">Non hai comprato un servizio.</p>
          <p className="text-white text-lg font-bold font-google mb-3">Hai investito nella visibilita' della TUA attivita' per i prossimi 12 mesi.</p>
          <p className="text-gray-400 text-xs">E ogni mese, ti mando un report per mostrarti i risultati. Nero su bianco.</p>
        </div>

      </div>
    </section>
  );
};

// Claim Business Section
const ClaimBusinessSection = () => {
  const steps = [
    { number: "1", title: "Cerca la tua attività", desc: "Digita su Google il nome della tua azienda + città", icon: Search, color: "bg-blue-500" },
    { number: "2", title: "Trova la scheda", desc: "Guarda il pannello sulla destra con le informazioni", icon: MapPin, color: "bg-green-500" },
    { number: "3", title: "Scorri in basso", desc: "Cerca il link \"Sei il proprietario di quest'attività?\"", icon: ChevronRight, color: "bg-yellow-500" },
    { number: "4", title: "Clicca e verifica", desc: "Se il link appare, la scheda NON è collegata al tuo account", icon: Shield, color: "bg-[#ff6b35]" },
  ];
  return (
    <section className="py-20 bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <Badge className="bg-[#ff6b35]/20 text-[#ff6b35] border-[#ff6b35]/30 mb-4"><MapPin className="w-4 h-4 mr-2" />Guida Pratica</Badge>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">La tua scheda Google è <span className="text-[#ff6b35]">davvero tua</span>?</h2>
          <p className="text-gray-400 max-w-3xl mx-auto">Ecco come scoprirlo in <strong className="text-white">30 secondi</strong>.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, index) => (
            <Card key={index} className="bg-gray-800/80 border-gray-700 hover:border-gray-500 transition-all">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 ${step.color} rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg`}>{step.number}</div>
                  <step.icon className="w-6 h-6 text-gray-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                <p className="text-gray-300 text-sm">{step.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="mt-12 bg-[#ff6b35]/10 border border-[#ff6b35]/30 rounded-xl p-6 text-center">
          <h4 className="text-lg font-bold text-white mb-2">Hai trovato il link?</h4>
          <p className="text-gray-400 text-sm mb-4">Ti aiuto a rivendicare la tua scheda in 72 ore.</p>
          <Button className="bg-[#ff6b35] hover:bg-[#e55a2b] rounded-full" onClick={() => window.open(`${WA_LINK}?text=Ciao! Ho trovato il link "Sei il proprietario" sulla mia scheda Google.`, '_blank')} data-testid="claim-whatsapp-btn">
            <MessageCircle className="w-4 h-4 mr-2" /> Scrivimi su WhatsApp
          </Button>
        </div>
      </div>
    </section>
  );
};

// Restaurant Section
const RestaurantSection = () => (
  <section id="ristoratori" className="py-20 bg-[#0c1220]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <Badge className="bg-orange-100 text-orange-600 border-orange-300 mb-6"><Utensils className="w-4 h-4 mr-2" />Speciale Ristoratori Siena</Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Elimina le Commissioni di TheFork</h2>
          <p className="text-gray-400 mb-8 leading-relaxed">Ti aiuto a configurare la tua scheda Google per ricevere prenotazioni dirette, eliminando le commissioni intermediarie.</p>
          <ul className="space-y-4 mb-8">
            {["Elimina le commissioni sulle prenotazioni TheFork", "Prenotazioni dirette dal tuo profilo Google", "Maggiore controllo sui tuoi clienti", "Due vantaggi con un investimento"].map((b, i) => (
              <li key={i} className="flex items-center gap-3 text-gray-300"><CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />{b}</li>
            ))}
          </ul>
          <Button size="lg" className="bg-[#ff6b35] hover:bg-[#e55a2b] rounded-full" onClick={() => window.open(CALENDLY, '_blank')} data-testid="restaurant-cta-btn">
            Scopri Come Risparmiare <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
        <Card className="bg-[#0a0e1a] border-white/10 shadow-xl">
          <CardContent className="p-8">
            <h3 className="text-xl font-semibold text-white mb-6">Un Intervento, Due Soluzioni</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                <h4 className="text-[#ff6b35] font-semibold mb-2">Ottimizzazione Scheda</h4>
                <p className="text-gray-400 text-sm">Assistenza premium, visibilita' e supporto dedicato</p>
              </div>
              <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                <h4 className="text-[#ff6b35] font-semibold mb-2">Zero Commissioni</h4>
                <p className="text-gray-400 text-sm">Prenotazioni dirette su Google Reserve</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  </section>
);

// Complete Guide Section
const CompleteGuideSection = () => {
  const guides = [
    { category: "Creazione e Configurazione", icon: Store, color: "text-blue-400", bgColor: "bg-blue-500/10",
      items: [{ title: "Come creare una scheda Google Business Profile", desc: "Ti guido passo passo nella creazione" }, { title: "Guida alla registrazione su Google My Business", desc: "Processo completo di registrazione" }] },
    { category: "Ottimizzazione e Visibilità", icon: TrendingUp, color: "text-yellow-500", bgColor: "bg-yellow-500/10",
      items: [{ title: "Come migliorare la visibilita' della scheda", desc: "Strategie per prime posizioni su Google Maps" }, { title: "Come ottenere piu' recensioni su Google", desc: "Metodi etici per aumentare le recensioni" }] },
    { category: "Situazioni Tecniche", icon: Shield, color: "text-red-500", bgColor: "bg-red-500/10",
      items: [{ title: "Perché la mia scheda non appare?", desc: "Diagnosi e soluzione per l'indicizzazione" }, { title: "Errori comuni nel Google Business Profile", desc: "Come correggere gli errori più frequenti" }] },
  ];
  return (
    <section className="py-20 bg-[#0a0e1a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <Badge className="bg-[#ff6b35]/20 text-[#ff6b35] border-[#ff6b35]/30 mb-4"><Search className="w-4 h-4 mr-2" />Guida Completa</Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Risolvo Ogni Situazione con la Tua Scheda Google</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {guides.map((guide, index) => (
            <Card key={index} className="bg-white/5 border-white/10 hover:border-[#ff6b35]/50 transition-all">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 ${guide.bgColor} rounded-xl flex items-center justify-center`}><guide.icon className={`w-5 h-5 ${guide.color}`} /></div>
                  <h3 className="font-bold text-white">{guide.category}</h3>
                </div>
                <ul className="space-y-3">
                  {guide.items.map((item, idx) => (
                    <li key={idx}><p className="text-sm font-medium text-gray-200">{item.title}</p><p className="text-xs text-gray-500 mt-1">{item.desc}</p></li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="text-center mt-12">
          <Button size="lg" className="bg-[#ff6b35] hover:bg-[#e55a2b] rounded-full" onClick={() => window.open(CALENDLY, '_blank')} data-testid="guide-cta-btn">
            Richiedi Assistenza Immediata <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default function RooftopPage() {
  return (
    <>
      <LuxuryPricingCard />
      {/* LocalSEO sections hidden — reserved for future use */}
      {/* <LocalSeoPresentationSection /> */}
      {/* <LocalSeoStatsSection /> */}
      <SectionDivider />
      <ClaimBusinessSection />
      <RestaurantSection />
      <CompleteGuideSection />
      <WhatsAppBookingForm pacchetto="Rooftop" color="#F59E0B" />
      <TShirtBundle pacchetto="Rooftop" />
      <CrossSellingGift pacchetto="Rooftop" />
      <CrossSellingProducts pacchetto="Rooftop" />
      <ContactSection />
    </>
  );
}
